// captionSlot.ts — the ONE shared speech-bubble slot (FACE&FURY S3b, gap 2).
//
// Two independent systems can want to "talk" over the board at the same moment: BYT3 the rival
// (rivalPersona.spawnCaption — a plated line + emote portrait) and a raced ghost (ghostTaunts.
// spawnGhostCaption — a plated taunt). Before this, both could fire in the same frame and stack two
// speech bubbles in the top-right corner (audit: BYT3 "Bleh! Decoy!" over YOUR GHOST "That's the
// mark."). This module serialises them so only ONE bubble is ever visible: a request runs immediately
// when the slot is free, otherwise it waits (higher priority first) at most MAX_QUEUE deep and anything
// past that DROPS — a slightly missed flavor line beats two overlapping ones.
//
// Renderer-only, determinism-free (wall-clock scene time, no Core/RNG/log touch). Per-scene state via a
// WeakMap so a board swap / scene teardown leaks nothing and never runs a stale build.
import type Phaser from 'phaser';

/** Builds the caption's GameObjects and returns its total on-screen lifetime in ms. */
export type CaptionBuild = (scene: Phaser.Scene) => number;

interface Pending {
  build: CaptionBuild;
  priority: number;
}
interface SlotState {
  busyUntil: number;
  queue: Pending[];
}

const SLOTS = new WeakMap<Phaser.Scene, SlotState>();
/** At most one caption may WAIT; a third concurrent request drops (freshness beats a backlog). */
const MAX_QUEUE = 1;
/** A small breath between two serialised captions so they never butt-join. */
const GAP_MS = 140;

function slotFor(scene: Phaser.Scene): SlotState {
  let st = SLOTS.get(scene);
  if (!st) {
    st = { busyUntil: 0, queue: [] };
    SLOTS.set(scene, st);
    // Clear any pending build on EVERY scene teardown so a shutdown never spawns into a dead scene.
    // MUST be `on`, not `once` (gap fix): Phaser reuses the GameScene instance across boards and the
    // WeakMap survives a shutdown (only 'destroy' clears it), so `once` reset only board 0 and left
    // boards 1-4 with a stale busyUntil — a caption queued at a board→Result handoff then permanently
    // stalled the slot, silencing every bark + taunt for the rest of that board. `on` registers once
    // (guarded by the `if (!st)` first-creation branch) yet fires on each board's shutdown.
    scene.events.on('shutdown', () => {
      const s = SLOTS.get(scene);
      if (s) {
        s.queue.length = 0;
        s.busyUntil = 0;
      }
    });
    scene.events.once('destroy', () => SLOTS.delete(scene));
  }
  return st;
}

/**
 * Defensive per-board reset (gap fix): drop any queued caption + free the slot at a new-board start.
 * Belt-and-braces alongside the shutdown listener — a board that begins with a clean slot can never
 * inherit a busyUntil far in the future (or a stranded pending build) from the prior board.
 */
export function resetCaptionSlot(scene: Phaser.Scene): void {
  const st = SLOTS.get(scene);
  if (st) {
    st.queue.length = 0;
    st.busyUntil = 0;
  }
}

/**
 * Request the shared caption slot. `build` creates the bubble and returns its lifetime; higher
 * `priority` wins a contended slot (BYT3's gatekeeper voice = 1 outranks a ghost taunt = 0).
 */
export function requestCaption(scene: Phaser.Scene, build: CaptionBuild, priority = 0): void {
  const st = slotFor(scene);
  if (scene.time.now >= st.busyUntil && st.queue.length === 0) {
    present(scene, st, build);
    return;
  }
  st.queue.push({ build, priority });
  st.queue.sort((a, b) => b.priority - a.priority);
  if (st.queue.length > MAX_QUEUE) st.queue.length = MAX_QUEUE; // drop the lowest-priority overflow
}

function present(scene: Phaser.Scene, st: SlotState, build: CaptionBuild): void {
  const life = Math.max(0, build(scene));
  st.busyUntil = scene.time.now + life + GAP_MS;
  scene.time.delayedCall(life + GAP_MS, () => {
    const next = st.queue.shift();
    if (next) present(scene, st, next.build);
  });
}

/** Priority constants so the two call sites share one vocabulary. */
export const CAPTION_PRIORITY = { rival: 1, ghost: 0 } as const;
